<script setup lang="ts">
/**
 * BlockFraudModal — 1:1 порт docs/mockups/modal-block-fraud.html (inbox-193).
 *
 * Size sm. Из antifraud-report incident row «Заблокировать».
 * Радио action: block_ip / block_visitor_fingerprint / block_referrer_domain
 * + reason text + summary card (incident fingerprint pre-filled).
 *
 * Payload: { incidentId, fingerprint: { ip?, asn?, userAgent?, referrer?, country? } }
 * Emit: 'block-fraud' { action, reason, incidentId }
 *
 * Backend pending — antifraud ML deferred (см. spec roadmap.md «v2»).
 */
import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

type BlockAction = 'block_ip' | 'block_fingerprint' | 'block_referrer';
const action = ref<BlockAction>('block_ip');
const reason = ref('');

interface Fingerprint {
  ip?: string; asn?: string; userAgent?: string; referrer?: string; country?: string;
}
interface Payload {
  incidentId: string | number;
  fingerprint: Fingerprint;
}
const payload = computed<Payload>(() => {
  const raw = (modals.payload || {}) as Record<string, unknown>;
  return {
    incidentId: (raw.incidentId as string | number) ?? 'preview',
    fingerprint: typeof raw.fingerprint === 'object' && raw.fingerprint !== null ? raw.fingerprint as Fingerprint : {
      ip: '185.220.101.42', asn: 'AS24940 Hetzner', userAgent: 'HeadlessChrome/121',
      referrer: 't.me/farm_clicks', country: 'DE',
    },
  };
});

interface ActionOpt { value: BlockAction; title: string; desc: string }
const ACTIONS: ActionOpt[] = [
  { value: 'block_ip',          title: 'IP-адрес',          desc: 'Заблокировать конкретный IP. Узкий scope.' },
  { value: 'block_fingerprint', title: 'Visitor fingerprint', desc: 'Хеш ip+ua+timezone. Шире — поймает spoofing.' },
  { value: 'block_referrer',    title: 'Referrer-домен',     desc: 'Блокировать клики с источника. Самый широкий scope.' },
];

const submitting = computed(() => modals.pending);
const canSubmit = computed(() => reason.value.trim().length >= 10);

function applyBlock() {
  if (!canSubmit.value) return;
  modals.setPending(true);
  modals.emit('block-fraud', {
    action: action.value,
    reason: reason.value.trim(),
    incidentId: payload.value.incidentId,
  });
  modals.setPending(false);
  modals.close();
}
</script>

<template>
  <UiModal title="Заблокировать подозрительный трафик" size="sm" :disable-close="submitting" @close="modals.close">
    <UiDevCard
      feature="Антифрод block — beta v2"
      message="Антифрод ML и blocks API доступны в beta v2 (см. roadmap.md). Сейчас отображается структура модалки."
    />

    <!-- Incident fingerprint -->
    <section class="fp-card">
      <div class="fp-head">
        <UiBadge variant="danger" dot>Инцидент #{{ payload.incidentId }}</UiBadge>
      </div>
      <dl class="fp-list">
        <div v-if="payload.fingerprint.ip" class="fp-row">
          <dt>IP</dt><dd><code>{{ payload.fingerprint.ip }}</code></dd>
        </div>
        <div v-if="payload.fingerprint.asn" class="fp-row">
          <dt>ASN</dt><dd>{{ payload.fingerprint.asn }}</dd>
        </div>
        <div v-if="payload.fingerprint.userAgent" class="fp-row">
          <dt>UA</dt><dd><code>{{ payload.fingerprint.userAgent }}</code></dd>
        </div>
        <div v-if="payload.fingerprint.referrer" class="fp-row">
          <dt>Referrer</dt><dd><code>{{ payload.fingerprint.referrer }}</code></dd>
        </div>
        <div v-if="payload.fingerprint.country" class="fp-row">
          <dt>Country</dt><dd>{{ payload.fingerprint.country }}</dd>
        </div>
      </dl>
    </section>

    <!-- Action selector -->
    <div class="action-section">
      <label class="field-label">Тип блокировки</label>
      <div class="action-list" role="radiogroup" aria-label="Тип блокировки">
        <label
          v-for="a in ACTIONS"
          :key="a.value"
          :class="['action-card', { selected: action === a.value }]"
        >
          <input type="radio" v-model="action" :value="a.value" />
          <div class="action-body">
            <span class="action-title">{{ a.title }}</span>
            <span class="action-desc">{{ a.desc }}</span>
          </div>
        </label>
      </div>
    </div>

    <!-- Reason -->
    <div class="reason-section">
      <label class="field-label" for="block-reason">Причина блокировки</label>
      <textarea
        id="block-reason"
        v-model="reason"
        class="input"
        rows="3"
        placeholder="Опишите паттерн: «click-farm с datacenter IPs, +1240 кликов за 8 минут»"
      ></textarea>
      <small class="reason-hint">Минимум 10 символов. Сохранится в audit-log.</small>
    </div>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="modals.close">Отмена</UiButton>
      <UiButton variant="danger" :disabled="submitting || !canSubmit" @click="applyBlock">
        {{ submitting ? 'Блокируем…' : 'Заблокировать' }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.fp-card {
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 8px; padding: 12px; margin-bottom: 14px;
}
.fp-head { margin-bottom: 8px; }
.fp-list { margin: 0; display: grid; grid-template-columns: 80px 1fr; gap: 6px 12px; }
.fp-row { display: contents; }
.fp-row dt { font-size: 11px; color: var(--fg-dim, #6b7480); text-transform: uppercase; letter-spacing: 0.04em; }
.fp-row dd { font-size: 12.5px; color: var(--fg, #e6eaf0); margin: 0; }
.fp-row dd code {
  font-family: var(--font-mono, ui-monospace, monospace); font-size: 11.5px;
  background: var(--bg, #0f1620); padding: 1px 6px; border-radius: 3px;
  color: var(--accent-hi, #6f9eff);
}

.action-section, .reason-section { margin-bottom: 14px; }
.field-label { display: block; font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); margin-bottom: 8px; }
.action-list { display: flex; flex-direction: column; gap: 6px; }
.action-card {
  display: flex; gap: 10px; padding: 8px 12px;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  border-radius: 7px; cursor: pointer; transition: 0.15s;
}
.action-card input { position: absolute; opacity: 0; pointer-events: none; }
.action-card.selected { border-color: #ff5d8f; background: rgba(255,93,143,0.06); }
.action-body { display: flex; flex-direction: column; gap: 2px; }
.action-title { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); }
.action-desc { font-size: 12px; color: var(--fg-muted, #9aa5b1); }

.input {
  width: 100%; box-sizing: border-box;
  padding: 9px 12px; background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340); border-radius: 7px;
  color: var(--fg, #e6eaf0); font: inherit; font-size: 13px;
  resize: vertical; min-height: 64px;
}
.input:focus { outline: none; border-color: var(--accent-hi, #6f9eff); }
.reason-hint { font-size: 11.5px; color: var(--fg-muted, #9aa5b1); margin-top: 4px; display: block; }
</style>
